import { createFileRoute } from '@tanstack/react-router'
import { useState, useCallback } from 'react'
import { ApiSearchableSelect } from '../../components/ApiSearchableSelect'
import type { ApiSearchableSelectOption } from '../../components/ApiSearchableSelect'
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../../components/ui/card'
import { Label } from '../../components/ui/label'
import { PageHeader } from '../../components/shared/PageHeader'
import { SECTION_ICONS } from '../../lib/section-icons'

export const Route = createFileRoute('/test/searcheable-select')({
  component: SearchableSelectTestPage,
})

// Mock data for testing
const mockCountries: ApiSearchableSelectOption[] = [
  { value: 'us', label: 'United States' },
  { value: 'ca', label: 'Canada' },
  { value: 'mx', label: 'Mexico' },
  { value: 'uk', label: 'United Kingdom' },
  { value: 'fr', label: 'France' },
  { value: 'de', label: 'Germany' },
  { value: 'it', label: 'Italy' },
  { value: 'es', label: 'Spain' },
  { value: 'jp', label: 'Japan' },
  { value: 'cn', label: 'China' },
  { value: 'in', label: 'India' },
  { value: 'au', label: 'Australia' },
  { value: 'br', label: 'Brazil' },
  { value: 'ar', label: 'Argentina' },
  { value: 'za', label: 'South Africa' },
]

const mockCities: ApiSearchableSelectOption[] = [
  { value: 'nyc', label: 'New York' },
  { value: 'lon', label: 'London' },
  { value: 'par', label: 'Paris' },
  { value: 'tok', label: 'Tokyo' },
  { value: 'syd', label: 'Sydney' },
]

function SearchableSelectTestPage() {
  // Test scenario 1: Basic API search
  const [value1, setValue1] = useState<string>('')
  const [selectedOption1, setSelectedOption1] = useState<ApiSearchableSelectOption | null>(null)

  // Test scenario 2: With initial options
  const [value2, setValue2] = useState<string>('')

  // Test scenario 3: Fast debounce
  const [value3, setValue3] = useState<string>('')

  // Test scenario 4: Min search length = 3
  const [value4, setValue4] = useState<string>('')

  // Test scenario 5: Disabled state
  const [value5, setValue5] = useState<string>('fr')

  // Test scenario 6: With allOptions fallback
  const [value6, setValue6] = useState<string>('')

  // Test scenario 7: No debounce
  const [value7, setValue7] = useState<string>('')

  // Mock API search function - simulates server delay
  const searchCountries = useCallback(async (query: string): Promise<ApiSearchableSelectOption[]> => {
    // Simulate API delay
    await new Promise(resolve => setTimeout(resolve, 500))

    const lowerQuery = query.toLowerCase()
    return mockCountries.filter(country =>
      country.label.toLowerCase().includes(lowerQuery) ||
      country.value.toLowerCase().includes(lowerQuery)
    )
  }, [])

  // Fast API search - shorter delay
  const searchCountriesFast = useCallback(async (query: string): Promise<ApiSearchableSelectOption[]> => {
    await new Promise(resolve => setTimeout(resolve, 200))

    const lowerQuery = query.toLowerCase()
    return mockCountries.filter(country =>
      country.label.toLowerCase().includes(lowerQuery)
    )
  }, [])

  // Search that returns many results
  const searchWithManyResults = useCallback(async (query: string): Promise<ApiSearchableSelectOption[]> => {
    await new Promise(resolve => setTimeout(resolve, 300))

    const lowerQuery = query.toLowerCase()
    const filtered = mockCountries.filter(country =>
      country.label.toLowerCase().includes(lowerQuery)
    )

    // Generate more results for testing scroll
    const extraResults: ApiSearchableSelectOption[] = []
    for (let i = 0; i < 20; i++) {
      extraResults.push({
        value: `extra-${i}`,
        label: `${query} Result ${i + 1}`,
      })
    }

    return [...filtered, ...extraResults]
  }, [])

  // Search that sometimes fails
  const searchWithErrors = useCallback(async (query: string): Promise<ApiSearchableSelectOption[]> => {
    await new Promise(resolve => setTimeout(resolve, 400))

    // Fail if query contains "error"
    if (query.toLowerCase().includes('error')) {
      throw new Error('Search failed')
    }

    const lowerQuery = query.toLowerCase()
    return mockCountries.filter(country =>
      country.label.toLowerCase().includes(lowerQuery)
    )
  }, [])

  return (
    <div className="container mx-auto p-6 max-w-6xl">
      <PageHeader
        className="mb-8"
        icon={SECTION_ICONS.debug}
        title="ApiSearchableSelect Test Page"
        description="Comprehensive testing of the ApiSearchableSelect component with various configurations"
      />

      <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
        {/* Test 1: Basic API Search */}
        <Card>
          <CardHeader>
            <CardTitle>Basic API Search</CardTitle>
            <CardDescription>
              Default configuration with 300ms debounce, min 1 character
            </CardDescription>
          </CardHeader>
          <CardContent className="space-y-4">
            <div className="space-y-2">
              <Label>Select Country</Label>
              <ApiSearchableSelect
                value={value1}
                onValueChange={setValue1}
                onOptionSelect={setSelectedOption1}
                onSearch={searchCountries}
                placeholder="Search countries..."
                searchPlaceholder="Type to search..."
                emptyMessage="No countries found"
                data-testid="select-basic"
              />
            </div>
            <div className="text-sm space-y-1">
              <div>
                <strong>Selected Value:</strong> {value1 || '(none)'}
              </div>
              {selectedOption1 && (
                <div>
                  <strong>Selected Label:</strong> {selectedOption1.label}
                </div>
              )}
            </div>
          </CardContent>
        </Card>

        {/* Test 2: With Initial Options */}
        <Card>
          <CardHeader>
            <CardTitle>With Initial Options</CardTitle>
            <CardDescription>
              Shows initial options before search, searches on API when typing
            </CardDescription>
          </CardHeader>
          <CardContent className="space-y-4">
            <div className="space-y-2">
              <Label>Select City</Label>
              <ApiSearchableSelect
                value={value2}
                onValueChange={setValue2}
                onSearch={searchCountries}
                initialOptions={mockCities}
                placeholder="Select or search..."
                searchPlaceholder="Search cities or countries..."
                data-testid="select-initial"
              />
            </div>
            <div className="text-sm">
              <strong>Selected Value:</strong> {value2 || '(none)'}
            </div>
          </CardContent>
        </Card>

        {/* Test 3: Fast Debounce */}
        <Card>
          <CardHeader>
            <CardTitle>Fast Debounce (100ms)</CardTitle>
            <CardDescription>
              Searches quickly as you type with minimal delay
            </CardDescription>
          </CardHeader>
          <CardContent className="space-y-4">
            <div className="space-y-2">
              <Label>Fast Search</Label>
              <ApiSearchableSelect
                value={value3}
                onValueChange={setValue3}
                onSearch={searchCountriesFast}
                debounceMs={100}
                placeholder="Fast search..."
                data-testid="select-fast"
              />
            </div>
            <div className="text-sm">
              <strong>Selected Value:</strong> {value3 || '(none)'}
            </div>
          </CardContent>
        </Card>

        {/* Test 4: Min Search Length */}
        <Card>
          <CardHeader>
            <CardTitle>Min Search Length = 3</CardTitle>
            <CardDescription>
              Requires at least 3 characters before searching
            </CardDescription>
          </CardHeader>
          <CardContent className="space-y-4">
            <div className="space-y-2">
              <Label>Type 3+ characters</Label>
              <ApiSearchableSelect
                value={value4}
                onValueChange={setValue4}
                onSearch={searchCountries}
                minSearchLength={3}
                placeholder="Min 3 chars..."
                data-testid="select-min-length"
              />
            </div>
            <div className="text-sm">
              <strong>Selected Value:</strong> {value4 || '(none)'}
            </div>
          </CardContent>
        </Card>

        {/* Test 5: Disabled State */}
        <Card>
          <CardHeader>
            <CardTitle>Disabled State</CardTitle>
            <CardDescription>
              Component is disabled with a pre-selected value
            </CardDescription>
          </CardHeader>
          <CardContent className="space-y-4">
            <div className="space-y-2">
              <Label>Disabled Select</Label>
              <ApiSearchableSelect
                value={value5}
                onValueChange={setValue5}
                onSearch={searchCountries}
                disabled={true}
                allOptions={mockCountries}
                placeholder="Disabled..."
                data-testid="select-disabled"
              />
            </div>
            <div className="text-sm">
              <strong>Selected Value:</strong> {value5 || '(none)'}
            </div>
          </CardContent>
        </Card>

        {/* Test 6: With allOptions fallback */}
        <Card>
          <CardHeader>
            <CardTitle>With allOptions Fallback</CardTitle>
            <CardDescription>
              Uses allOptions to display selected label even if not in search results
            </CardDescription>
          </CardHeader>
          <CardContent className="space-y-4">
            <div className="space-y-2">
              <Label>Select with Fallback</Label>
              <ApiSearchableSelect
                value={value6}
                onValueChange={setValue6}
                onSearch={searchCountries}
                allOptions={mockCountries}
                placeholder="Search with fallback..."
                data-testid="select-fallback"
              />
            </div>
            <div className="text-sm">
              <strong>Selected Value:</strong> {value6 || '(none)'}
            </div>
          </CardContent>
        </Card>

        {/* Test 7: No Debounce */}
        <Card>
          <CardHeader>
            <CardTitle>No Debounce (0ms)</CardTitle>
            <CardDescription>
              Searches immediately on every keystroke
            </CardDescription>
          </CardHeader>
          <CardContent className="space-y-4">
            <div className="space-y-2">
              <Label>Instant Search</Label>
              <ApiSearchableSelect
                value={value7}
                onValueChange={setValue7}
                onSearch={searchCountriesFast}
                debounceMs={0}
                placeholder="Instant search..."
                data-testid="select-no-debounce"
              />
            </div>
            <div className="text-sm">
              <strong>Selected Value:</strong> {value7 || '(none)'}
            </div>
          </CardContent>
        </Card>

        {/* Test 8: Many Results (Scrolling) */}
        <Card>
          <CardHeader>
            <CardTitle>Many Results (Scroll Test)</CardTitle>
            <CardDescription>
              Returns many results to test scrolling behavior
            </CardDescription>
          </CardHeader>
          <CardContent className="space-y-4">
            <div className="space-y-2">
              <Label>Search with Many Results</Label>
              <ApiSearchableSelect
                value=""
                onValueChange={() => {}}
                onSearch={searchWithManyResults}
                placeholder="Type anything..."
                data-testid="select-many-results"
              />
            </div>
          </CardContent>
        </Card>

        {/* Test 9: Error Handling */}
        <Card>
          <CardHeader>
            <CardTitle>Error Handling</CardTitle>
            <CardDescription>
              Type "error" to trigger a search failure
            </CardDescription>
          </CardHeader>
          <CardContent className="space-y-4">
            <div className="space-y-2">
              <Label>Search with Errors</Label>
              <ApiSearchableSelect
                value=""
                onValueChange={() => {}}
                onSearch={searchWithErrors}
                placeholder="Type 'error' to fail..."
                emptyMessage="Search failed - try again"
                data-testid="select-errors"
              />
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Testing Instructions */}
      <Card className="mt-6">
        <CardHeader>
          <CardTitle>Testing Instructions</CardTitle>
        </CardHeader>
        <CardContent className="space-y-4">
          <div>
            <h3 className="font-semibold mb-2">What to Test:</h3>
            <ul className="list-disc list-inside space-y-1 text-sm text-muted-foreground">
              <li>Loading states - observe the spinner while searching</li>
              <li>Debouncing - notice the delay before search triggers</li>
              <li>Minimum search length - validation message appears</li>
              <li>Clear button - appears when value is selected, click to clear</li>
              <li>Initial options - shown before typing in scenario 2</li>
              <li>Disabled state - cannot interact with the select</li>
              <li>Empty states - search for something that doesn't exist</li>
              <li>Error handling - type "error" in the last scenario</li>
              <li>Selection - click to select, click again to deselect</li>
              <li>Keyboard navigation - use arrow keys and Enter</li>
              <li>Focus behavior - input auto-focuses when dropdown opens</li>
              <li>Scrolling - test with many results scenario</li>
            </ul>
          </div>
          <div>
            <h3 className="font-semibold mb-2">Console Logs:</h3>
            <p className="text-sm text-muted-foreground">
              Open the browser console to see useEffect logs showing when effects trigger
            </p>
          </div>
        </CardContent>
      </Card>
    </div>
  )
}
